<!--
  Default row renderer for the legacy (non-BUI) list path used inside the
  select-from-lister dialogs (pick a Location / Organization). Historically
  `TableViewItem` was only provided by list pages that pass a bespoke
  component; the select dialogs pass none, so `ItemsLister` provided
  `undefined` and every data cell rendered empty — leaving the picker as
  unlabeled rows the user couldn't read.

  This generic component renders one <td> per column from the API-driven
  `columnsConfig` (aligning with the SortableTableHeaders header), pulling
  each value with `column.fieldName` (the camelCase item key; `columnName`
  is the PascalCase sort key and does NOT match the row data). The first
  column is emphasised as the item's name so a row is always identifiable.
-->
<template>
  <td
    v-for="(column, colIndex) in visibleColumns"
    :key="column.fieldName || column.columnName || colIndex"
    :class="['generic-cell', { 'generic-cell--name': colIndex === 0 }]"
  >
    {{ cellText(column) }}
  </td>
</template>

<script lang="ts" setup>
import _get from 'lodash/get';
import { computed } from 'vue';

const props = defineProps<{
  item: any;
  itemIndex?: any;
  columnsConfig?: any;
  renderers?: any;
  apiPathsConfig?: any;
  listerConfig?: any;
  selectMode?: boolean;
  singleSelect?: boolean;
}>();

// Skip columns without a resolvable key so the row can't silently misalign.
const visibleColumns = computed(() =>
  (props.columnsConfig || []).filter((c: any) => c && (c.fieldName || c.columnName)),
);

/** Render a column's value as readable text; never dump raw objects. */
function cellText(column: any): string {
  // fieldName is the camelCase item key; columnName is the PascalCase sort key.
  const value = _get(props.item, column.fieldName ?? column.columnName);
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') {
    // Prefer a nested name/label if present, otherwise fall back to a dash.
    return value.name ?? value.label ?? value.displayName ?? '—';
  }
  return String(value);
}
</script>

<style lang="scss" scoped>
.generic-cell {
  word-break: break-word;
  color: #262626;
}

.generic-cell--name {
  font-weight: bold;
  min-width: 10rem;
}
</style>
